import Typography from 'typography'

import { black, orange, darkgreen } from './colors'

export const baseFontSize = 22
export const linkBorderWeight = '5px'
export const accent = darkgreen
export const accentMore = orange

const boxShadow =
  '0 20px 40px rgba(0, 0, 0, 0.24), 0 40px 60px rgba(0, 0, 0, 0.12)'

const typography = new Typography({
  baseFontSize: `${baseFontSize}px`,
  baseLineHeight: 1.5,
  scaleRatio: 2,
  headerFontFamily: ['laffayette_comic_proregular', 'sans-serif'],
  bodyFontFamily: ['sans-serif'],
  bodyWeight: 300,
  overrideStyles: ({ adjustFontSizeTo, rhythm }, options, styles) => ({
    /* typography */
    a: {
      color: black,
      textDecoration: 'none',
      transition: 'color 0.3s, border-color 0.3s',
    },
    'a:visited': {
      color: 'inherit',
    },
    'a:focus, a:hover': {
      color: accentMore,
    },
    'article a:not([class])': {
      borderBottom: `${linkBorderWeight} solid ${accent}`,
    },
    'article a:not([class]):focus, article a:not([class]):hover': {
      borderColor: accentMore,
    },
    'article a.footnote-ref': {
      borderBottom: `${linkBorderWeight} solid ${accent}`,
    },
    'article a:footnote-ref:hover, article a:footnote-ref:focus': {
      borderColor: accentMore,
    },
    'article h1 a:not([class]), h2 a:not([class])': {
      borderBottom: 'none',
    },
    'article h1 a:not([class]):hover, h2 a:not([class]):hover, article h1 a:not([class]):focus, h2 a:not([class]):focus':
      {
        borderBottom: 'none',
      },
    'article h1, article h2, article h3, article h4, article h5': {
      // make it easy to place anchors with position absolute
      position: 'relative',
    },

    blockquote: {
      borderLeft: '4px solid lightgrey',
      padding: rhythm(1),
      margin: `${rhythm(1)} 0 `,
      background: 'linear-gradient(to right, #efefef, #f9f9f9)',
      borderWidth: '1px 1px 1px 6px',
      borderColor: 'lightgrey lightgrey lightgrey grey',
      borderStyle: 'solid',
    },
    'blockquote footer': {
      textAlign: 'right',
    },

    /* lists */
    li: {
      marginBottom: 0,
    },
    'li > ul, li > ol': {
      marginBottom: 0,
      marginTop: 0,
    },
    'li p': {
      marginBottom: 0,
    },

    /* special components */
    // These callout components styles need to be here
    // in order to be able to use it in markdown. There's
    // probably a better way to handle this

    '.callout': {
      border: '2px solid black',
      backgroundColor: '#fffacd',
      padding: `${rhythm(1.25)} ${rhythm(1)} 0 ${rhythm(1)}`,
      color: '#333',
      textAlign: 'center',
      margin: `${rhythm(1)} 0`,
      clear: 'both',
    },

    '.callout.blue': {
      /*
      This is what happens when you use stupid class names
      backgroundColor: 'aliceblue',
      borderColor: 'steelblue',
      */
      // greenish shade
      backgroundColor: '#f5fffa',
      borderColor: '#00994f',
    },
    '.callout.info': {
      backgroundColor: '#f5fffa',
      borderColor: '#00994f',
    },

    /* code styles */
    '.gatsby-highlight pre[class*="language-"]': {
      margin: `0 0 ${rhythm(1)} 0`,
    },
    ':not(pre) > code[class*="language-"]': {
      padding: '3px 5px !important',
    },

    /* Markdown styles */
    '.photo-album': {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
    },
    'img.center, .gatsby-image-wrapper.center': {
      display: 'block',
      margin: `${rhythm(1)} auto ${rhythm(2)}`,
    },
    'img.center-uniform': {
      display: 'block',
      margin: `${rhythm(1)} auto ${rhythm(1)}`,
    },
    'img.no-margin': {
      margin: 0,
    },
    'img.center-only': {
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    'img.highlight': {
      transition: 'transform 0.5s',
    },
    'img.highlight:hover, img.highlight:focus': {
      transform: 'rotate(3deg)',
    },
    'img.left': {
      float: 'left',
    },
    'img.right': {
      float: 'right',
    },
    'img.margin-small': {
      margin: rhythm(1 / 15),
    },
    'img.center.margin-small': {
      margin: `${rhythm(1)} auto ${rhythm(1 / 15)}`,
    },
    'img.margin-big': {
      margin: `${rhythm(1)} ${rhythm(1)} ${rhythm(1)} ${rhythm(2)}`,
    },
    'img.margin-big.left': {
      margin: `${rhythm(1)} ${rhythm(1.5)} ${rhythm(1.5)} ${rhythm(2)}`,
    },
    'img.shadow-small': {
      boxShadow,
    },
    'img.book': {
      display: 'block',
      margin: `${rhythm(1)} auto ${rhythm(2)}`,
      boxShadow,
      transition: 'transform 0.5s',
    },
    'img.photo': {
      border: '6px solid white',
    },
    'img.rotate-right': {
      transform: 'rotate(3deg)',
    },
    'img.book:hover, img.book:focus': {
      transform: 'rotate(3deg)',
    },
    '.clear': {
      clear: 'both',
    },
    video: {
      marginTop: `${rhythm(2)}`,
      marginBottom: `${rhythm(2)}`,
    },
    'video.margin-small': {
      marginBottom: 0,
    },
    'video.center': {
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    'blockquote cite': {
      fontSize: '0.7em',
      display: 'block',
      textAlign: 'right',
      fontWeight: 'bold',
    },
    // Anchor in headers
    'a.anchor': {
      position: 'absolute',
      color: accent,
      left: '-18px',
    },
    'a.anchor:hover, a.anchor:focus': {
      color: accentMore,
    },
    'a.anchor::before': {
      content: '"#"',
      color: 'inherit',
    },
    'a.anchor svg': {
      display: 'none',
    },
    'blockquote a.anchor': {
      left: '-6px',
    },
    audio: {
      display: 'block',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
    },
  }),
})

// Hot reload typography in development.
if (process.env.NODE_ENV !== 'production') {
  typography.injectStyles()
}

const { rhythm, scale } = typography
export { rhythm, scale, typography as default }
